import { getAllWidgetDefs } from "./widgetRegistry";
import { computeContractHash, NoDuplication, NoInfo, NoControlPanel } from "./widgetContract";
import { pdfSupport } from "./widgetDefinition";
import type { WidgetDefinition } from "./widgetDefinition";
import { isNoSlots } from "./Slots";

// Registry-walking side of the contract. Kept out of `widgetContract.ts` so the
// definitions can import the NoX sentinels without a load-time cycle.

export type ContractStatusKind = "wired" | "optout" | "missing" | "pending";

export interface ContractStatus {
  kind: ContractStatusKind;
  /** Short tag text — e.g. "Wired", "NoInfo", "Missing", "Coming soon". */
  label: string;
  /** One sentence describing the widget's CURRENT state for this concern. */
  detail: string;
  /** Red bold caveat shown when this widget opted out of the concern (NoX). */
  warning?: string;
}

export interface ChecklistItem {
  key: string;
  label: string;
  /** What this concern IS — the plain-language explanation shown under the item. */
  description: string;
  /** Optional bolded caution shown beneath the description. */
  caution?: string;
  /** True when the definition satisfies this concern (handler present, or explicit NoX). */
  complete: (def: WidgetDefinition) => boolean;
  /** This widget's current wiring for the concern (wired / opt-out / missing / not-yet-enabled). */
  status: (def: WidgetDefinition) => ContractStatus;
  /** false → shown greyed as a not-yet-enforced placeholder (does not gate the hash). */
  required?: boolean;
}

const WIRED = (detail: string): ContractStatus => ({ kind: "wired", label: "Wired", detail });
const OPTOUT = (label: string, detail: string, warning: string): ContractStatus => ({ kind: "optout", label, detail, warning });
const MISSING = (detail: string): ContractStatus => ({ kind: "missing", label: "Missing", detail });
const PENDING = (label: string, detail: string): ContractStatus => ({ kind: "pending", label, detail });

/** The concerns the checklist enforces, in display order. */
export const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    key: "render",
    label: "Widget renderer + process",
    required: true,
    description:
      "The React component drawn in the grid, plus the process() that turns the raw API response into this widget's data. Mandatory for every widget.",
    complete: (d) => !!d.gridComponent,
    status: () => WIRED("Grid component and data processing are defined."),
  },
  {
    key: "duplicate",
    label: "Duplication handler",
    required: true,
    description:
      "Regenerates the spec's data queryIds when a widget is duplicated or imported, so the copy fetches its own data. Opt out with NoDuplication when the widget has no queryIds.",
    caution:
      "Certain components may require custom duplication logic. Consider the needs of your component carefully!",
    complete: (d) => d.duplicate !== undefined,
    status: (d) =>
      d.duplicate === undefined ? MISSING("No duplication handler is wired.")
      : d.duplicate === NoDuplication ? OPTOUT("NoDuplication", "Opted out — this widget has no queryIds to regenerate.", "IMPORTANT: This component has no associated duplication logic. You should be sure that this is intended behavior.")
      : WIRED("Regenerates the spec's queryIds when the widget is duplicated or imported."),
  },
  {
    key: "info",
    label: "Info popover",
    required: true,
    description:
      "The ⓘ hover popover in the widget header summarizing the current date range, subjects and metrics. Opt out with NoInfo when the widget shows no popover.",
    complete: (d) => d.info !== undefined,
    status: (d) =>
      d.info === undefined ? MISSING("No info handler is wired.")
      : d.info === NoInfo ? OPTOUT("NoInfo", "Opted out — this widget has no header info popover.", "IMPORTANT: This component has no associated info popover. You should be sure that this is intended behavior.")
      : WIRED("Renders a custom info popover in the widget header."),
  },
  {
    key: "controlPanel",
    label: "Control panel handler",
    required: true,
    description:
      "How the dashboard Control Panel's bulk date/subject apply mutates this widget's spec. Opt out with NoControlPanel when the widget isn't affected by bulk apply.",
    caution:
      "Certain components may require custom control-panel logic. Consider the needs of your component carefully!",
    complete: (d) => d.controlPanel !== undefined,
    status: (d) =>
      d.controlPanel === undefined ? MISSING("No control-panel handler is wired.")
      : d.controlPanel === NoControlPanel ? OPTOUT("NoControlPanel", "Opted out — the Control Panel's bulk apply skips this widget.", "IMPORTANT: This component has no associated control panel behavior. You should be sure that this is intended behavior.")
      : WIRED("Applies the Control Panel's bulk date/subject selection to this widget."),
  },
  {
    key: "slots",
    label: "Template slots",
    required: true,
    description:
      "The id-bearing locations in this widget's spec (metric / subject / drill ids) that the dashboard template (de)serializer tokenizes on export and resolves on import. Opt out with NoSlots when the widget carries no such ids.",
    complete: (d) => d.slots !== undefined,
    status: (d) =>
      isNoSlots(d.slots) ? OPTOUT("NoSlots", "Opted out — this widget has no metric/subject/drill ids to tokenize.", "IMPORTANT: This component exposes no template slots. You should be sure that this is intended behavior.")
      : WIRED("Exposes its metric/subject/drill id locations to the template tokenizer and resolver."),
  },
  {
    key: "pdf",
    label: "PDF export",
    required: true,
    description:
      "PDF export support: a snapshot builder + renderer, and how the widget's image area is captured (Highcharts, DOM, or none). Opt out with NoPdf when the widget shouldn't appear in PDF.",
    complete: (d) => d.pdf !== undefined,
    status: (d) => {
      if (d.pdf === undefined) return MISSING("No PDF support is wired.");
      const support = pdfSupport(d.pdf);
      if (!support) return OPTOUT(
        "NoPdf",
        "Opted out — this widget is skipped in PDF export.",
        "IMPORTANT: This component has no associated PDF export. You should be sure that this is intended behavior.",
      );
      return WIRED(
        support.capture === "highcharts" ? "Builds a snapshot, renders into the PDF, and captures a Highcharts chart image."
        : support.capture === "dom" ? "Builds a snapshot, renders into the PDF, and rasterizes the widget body (html2canvas)."
        : "Builds a snapshot and renders into the PDF export (no image capture needed).",
      );
    },
  },
  {
    key: "describe",
    label: "AI description",
    required: false,
    description:
      "A natural-language summary of the widget used to prompt the AI \"Build my Dashboard\" feature. Not yet enabled.",
    complete: (d) => d.describe !== undefined,
    status: (d) =>
      d.describe ? WIRED("Description provided.")
      : PENDING("Coming soon", "AI dashboard-builder descriptions aren't enabled yet."),
  },
  {
    key: "dynamic",
    label: "Dynamic behavior",
    required: false,
    description:
      "Reactive / dynamic behavior hooks for the widget. Not yet enabled.",
    complete: (d) => d.dynamic !== undefined,
    status: (d) =>
      d.dynamic ? WIRED("Dynamic behavior configured.")
      : PENDING("Coming soon", "Dynamic behavior isn't enabled yet."),
  },
];

export interface ContractProblem {
  type: string;
  problems: string[];
}

/** Walk the registry; return every widget with a missing required handler or a stale hash. */
export function checkWidgetContract(): ContractProblem[] {
  const out: ContractProblem[] = [];
  for (const def of getAllWidgetDefs()) {
    const problems: string[] = [];
    for (const item of CHECKLIST_ITEMS) {
      if (item.required && !item.complete(def)) problems.push(item.label);
    }
    if (def.contractHash == null) problems.push("contractHash missing — run the checklist");
    else if (def.contractHash !== computeContractHash(def)) problems.push("schema changed — re-run the checklist");
    if (problems.length) out.push({ type: def.type, problems });
  }
  return out;
}
